import {
  Button,
  ClipboardIcon,
  DocumentDuplicateIcon,
  Flex,
  IconButton,
  Popover,
  Switch,
  Text,
  Tooltip,
  toaster,
} from 'components'
import { produce } from 'immer'
import { useState } from 'react'
import { ILayout } from 'types'
import { containerLayoutTypes } from '../../../utils/templateBuilderUtils'
import { useTemplate } from '../TemplateProvider'
import { useTemplateLayout } from './TemplateLayoutProvider'

interface Props {}

export default function CopyPasteLayout({}: Props) {
  const { template, updateTemplate } = useTemplate()
  const {
    selectedLayout,
    setLayout,
    parentLayout,
    clipboardLayoutKeyPath,
    setClipboardLayoutKeyPath,
    setSelectedKeyPath,
  } = useTemplateLayout()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [asReference, setAsReference] = useState(false)

  if (!selectedLayout) return null

  const clipboardParentPathKeys = clipboardLayoutKeyPath?.split('/').map(Number) ?? []
  const clipboardLayoutKey = clipboardLayoutKeyPath ? clipboardParentPathKeys.pop() : undefined
  const clipboardParentKey = clipboardParentPathKeys.length
    ? clipboardParentPathKeys[clipboardParentPathKeys.length - 1]
    : undefined
  const selectedLayoutKeys = selectedLayout.keyPath.split('/').map(Number)
  const selectedKey = selectedLayoutKeys[selectedLayoutKeys.length - 1]
  const selectedIsInClipboardPath =
    clipboardLayoutKey && selectedLayoutKeys.includes(clipboardLayoutKey)
  const isContainer = containerLayoutTypes.includes(selectedLayout.type)
  const sameParent = clipboardParentPathKeys.join('/') === selectedLayout.keyPath

  const enablePaste = Boolean(
    clipboardLayoutKeyPath &&
      !selectedIsInClipboardPath &&
      selectedKey !== clipboardLayoutKey &&
      (isContainer || !selectedLayout.action)
  )

  function handleCopy() {
    if (!selectedKey || !selectedLayout) return

    setClipboardLayoutKeyPath(selectedLayout.keyPath)
    toaster.create({
      title: 'Layout copied to clipboard',
      description: 'Navigate to desired parent layout and paste',
      type: 'success',
    })
  }

  function handlePasteReference() {
    if (!clipboardLayoutKey) return

    const updatedLayout = addReferenceToLayout(clipboardLayoutKey)
    if (!updatedLayout) return

    setLayout(updatedLayout)
    setIsDialogOpen(false)
    setSelectedKeyPath(updatedLayout.keyPath + `/${clipboardLayoutKey}`)
  }

  function handleGenerateNew() {
    if (!selectedLayout || !template?.layoutMap || !clipboardLayoutKey) return

    const { newLayoutMap, newRootKey } = generateNewLayoutKeys(
      template.layoutMap,
      clipboardLayoutKey
    )

    const updatedLayout = addReferenceToLayout(newRootKey)
    if (!updatedLayout) return

    const { keyPath, ...rest } = updatedLayout
    updateTemplate({ layoutMap: { ...newLayoutMap, [selectedKey]: rest } })
    setIsDialogOpen(false)
    setSelectedKeyPath(keyPath + `/${newRootKey}`)
  }

  function addReferenceToLayout(key: number) {
    if (!selectedLayout || !clipboardParentKey || !template?.layoutMap) return

    const clipboardParent = template.layoutMap[clipboardParentKey]
    if (!clipboardParent) return

    return produce(selectedLayout, draft => {
      const action =
        clipboardParent.action?.layoutKey === clipboardLayoutKey
          ? clipboardParent.action
          : undefined

      if (action) {
        draft.action = { ...action, layoutKey: key }
      } else if ('layouts' in draft) {
        draft.layouts.push({ key })
      }
    })
  }

  if (!selectedKey) return null

  return (
    <Flex gap={2}>
      {parentLayout && (
        <Tooltip content="Copy layout">
          <IconButton
            size="sm"
            color="primary.500"
            aria-label="Copy layout"
            icon={<ClipboardIcon type="outline" size="sm" />}
            onClick={handleCopy}
          />
        </Tooltip>
      )}

      {enablePaste && (
        <Popover
          header="Paste Layout"
          trigger={
            <Tooltip content="Paste layout">
              <IconButton
                aria-label="Paste layout"
                color="primary.500"
                icon={<DocumentDuplicateIcon type="outline" size="sm" />}
                onClick={() => (sameParent ? handleGenerateNew() : setIsDialogOpen(true))}
                size="sm"
              />
            </Tooltip>
          }
          positioning={{ placement: 'bottom-end' }}
          portal
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
        >
          <Flex direction="column" gap={4} p={4}>
            <Switch checked={asReference} onChange={setAsReference} label="Paste as reference" />

            <Text>
              {asReference ? 'Edits will reflect in original' : 'New Layout will be a new copy'}
            </Text>
            <Flex justifyContent="flex-end" gap={2}>
              <Button variant="ghost" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                palette="primary"
                onClick={asReference ? handlePasteReference : handleGenerateNew}
                p={4}
                leftIcon={<DocumentDuplicateIcon size="sm" />}
              >
                Paste {asReference ? 'Reference' : 'New Copy'}
              </Button>
            </Flex>
          </Flex>
        </Popover>
      )}
    </Flex>
  )
}

function generateNewLayoutKeys(originalLayoutMap: Record<number, ILayout>, clipboardKey: number) {
  const keyMap = new Map<number, number>()
  const newLayoutMap = { ...originalLayoutMap }

  // Find the highest key to start generating new ones
  let nextKey = Math.max(...Object.keys(newLayoutMap).map(Number)) + 1

  const copyLayoutRecursive = (layoutKey: number): number => {
    const originalLayout = originalLayoutMap[layoutKey]
    if (!originalLayout) return layoutKey

    // Generate new key if not already mapped
    if (!keyMap.has(layoutKey)) {
      keyMap.set(layoutKey, nextKey++)
    }

    const newKey = keyMap.get(layoutKey)!
    const newLayout = produce(originalLayout, draft => {
      // Copy child layouts with new keys
      if ('layouts' in draft && draft.layouts) {
        draft.layouts = draft.layouts.map(item => ({
          ...item,
          key: item.key ? copyLayoutRecursive(item.key) : item.key,
        }))
      }

      // Copy action layout with new key
      if (draft.action?.layoutKey) {
        draft.action.layoutKey = copyLayoutRecursive(draft.action.layoutKey)
      }
    })

    newLayoutMap[newKey] = newLayout
    return newKey
  }

  const newRootKey = copyLayoutRecursive(clipboardKey)
  return { newLayoutMap, newRootKey }
}
